import { useState } from "react";

/**
 * **Zooming into the day pages the hours rather than scrolling them** (CZ-D1,
 * Emile, 2026-10-21).
 *
 * FS-D1 fits the whole day to the window, so a zoom that only changed the
 * scale was clamped straight back to fit — the location's 40–150% slider did
 * nothing above about 40% on a laptop. Instead, zooming in shows *fewer hours*,
 * each taller, and the reader pages through the day: nothing scrolls, which
 * keeps FS-D1 and the no-scrolling rule.
 *
 * The steps are hours on screen; `null` is the whole day.
 */
export type HoursShown = number | null;

/**
 * **Three fixed views beside the hours** (CZ-D5, Emile, 2026-10-21: *"give me
 * the option to see the full day as well, as well as AM and PM"*): the whole
 * day squeezed to the window whatever the minimum hour (CZ-D4), the morning up
 * to 12:00, and the afternoon from it. Each fills the window and is not paged.
 */
export type Preset = "full" | "am" | "pm";

/** What the person chose: a number of hours (or the most that fit), or a preset. */
export type Zoom = HoursShown | Preset;

export const PRESETS: readonly { value: Preset; label: string }[] = [
  { value: "full", label: "Full day" },
  { value: "am", label: "AM" },
  { value: "pm", label: "PM" },
];

export function isPreset(zoom: Zoom): zoom is Preset {
  return typeof zoom === "string";
}

const NOON = 12 * 60;

/** The morning or the afternoon of a day, never empty: at least its first or last hour. */
export function presetWindow(
  day: { startsAt: number; endsAt: number },
  preset: "am" | "pm",
): HourWindow {
  const noon = Math.min(Math.max(NOON, day.startsAt + 60), day.endsAt - 60);
  return preset === "am"
    ? {
        startsAt: day.startsAt,
        endsAt: Math.max(noon, day.startsAt + 60),
        earlier: false,
        later: false,
      }
    : {
        startsAt: Math.min(noon, day.endsAt - 60),
        endsAt: day.endsAt,
        earlier: false,
        later: false,
      };
}

export const ZOOM_STEPS: readonly HoursShown[] = [null, 8, 6, 4, 3, 2];

const STORAGE_KEY = "dc.calendar.hoursShown";

/**
 * How many hours a grid can draw at the minimum hour height, and how many its
 * day has (CZ-D4). Null until the grid has measured itself.
 */
export interface Capacity {
  fits: number;
  day: number;
}

/**
 * The hours actually drawn: the chosen zoom, never more than fit (CZ-D4).
 * Null is the whole day, which only happens when the whole day fits.
 */
export function effectiveHours(chosen: HoursShown, capacity: Capacity | null): HoursShown {
  if (capacity === null) return chosen;
  const hours = Math.max(1, Math.min(chosen ?? capacity.day, capacity.fits));
  return hours >= capacity.day ? null : hours;
}

/**
 * The step one further in (`+1`) or out (`-1`) from what is drawn, staying at
 * the ends. Out past what fits is the most that fits, chosen as `null`.
 */
export function stepZoom(
  current: HoursShown,
  direction: 1 | -1,
  capacity: Capacity | null = null,
): HoursShown {
  const drawn = effectiveHours(current, capacity) ?? Infinity;
  const steps = ZOOM_STEPS.map((step) => step ?? Infinity);
  if (direction === 1) {
    const next = steps.find((step) => step < drawn);
    return next === undefined ? current : next;
  }
  const most = capacity === null ? Infinity : capacity.fits;
  const next = [...steps].reverse().find((step) => step > drawn);
  return next === undefined || next >= most ? null : next;
}

/** Whether a step out would show anything more. */
export function canZoomOut(current: HoursShown, capacity: Capacity | null): boolean {
  return effectiveHours(current, capacity) !== null && current !== null;
}

export function zoomLabel(hours: HoursShown): string {
  return hours === null ? "Whole day" : `${String(hours)} hours`;
}

export interface HourWindow {
  startsAt: number;
  endsAt: number;
  /** Whether there is more of the day above and below. */
  earlier: boolean;
  later: boolean;
}

/**
 * The hours drawn, out of the day's `[startsAt, endsAt]`.
 *
 * Null when the whole day is shown — `hours` is null, or the day is no longer
 * than the window. `from` is where the reader paged to; with none, **today
 * opens on the hour you are in** (FX14) and any other day at its start. Always
 * whole hours, and never past either end of the day.
 */
export function windowOf(
  day: { startsAt: number; endsAt: number },
  hours: HoursShown,
  from: number | null,
  nowMinute: number | null,
): HourWindow | null {
  if (hours === null) return null;
  const length = hours * 60;
  if (length >= day.endsAt - day.startsAt) return null;

  const wanted = from ?? (nowMinute === null ? day.startsAt : Math.floor(nowMinute / 60) * 60);
  const startsAt = Math.min(Math.max(wanted, day.startsAt), day.endsAt - length);
  return {
    startsAt,
    endsAt: startsAt + length,
    earlier: startsAt > day.startsAt,
    later: startsAt + length < day.endsAt,
  };
}

/**
 * One page earlier or later: the window less an hour, so the hour at the edge
 * stays on screen and the reader keeps their place. Two hours shown page by one.
 */
export function pageStep(hours: number): number {
  return Math.max(1, hours - 1) * 60;
}

/** Whole hours from `startsAt` to `endsAt` inclusive, for an axis. */
export function hoursBetween(startsAt: number, endsAt: number): number[] {
  const hours: number[] = [];
  for (let minute = startsAt; minute <= endsAt; minute += 60) hours.push(minute);
  return hours;
}

/**
 * The zoom, per person and remembered on this computer — a way of looking, not
 * a setting of the location, so it is not stored on the server.
 */
export function useHoursShown(): [Zoom, (next: Zoom) => void] {
  const [hours, setHours] = useState<Zoom>(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      const preset = PRESETS.find((option) => option.value === saved);
      if (preset !== undefined) return preset.value;
      const parsed = saved === null || saved === "" ? null : Number(saved);
      return ZOOM_STEPS.includes(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });
  const set = (next: Zoom): void => {
    setHours(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next === null ? "" : String(next));
    } catch {
      // A blocked store only means the zoom is not remembered.
    }
  };
  return [hours, set];
}

/**
 * Where the reader has paged to, forgotten when the day or the zoom changes so
 * each lands afresh. Returns the window and a pager.
 */
export function usePagedWindow(
  day: { startsAt: number; endsAt: number },
  hours: HoursShown,
  date: string,
  nowMinute: number | null,
): { window: HourWindow | null; page: (direction: 1 | -1, by?: number) => void } {
  const key = `${date}|${String(hours)}`;
  const [paged, setPaged] = useState<{ key: string; from: number } | null>(null);
  const from = paged?.key === key ? paged.from : null;
  const window = windowOf(day, hours, from, nowMinute);

  const page = (direction: 1 | -1, by?: number): void => {
    if (window === null || hours === null) return;
    const next = window.startsAt + direction * (by ?? pageStep(hours));
    const clamped = Math.min(Math.max(next, day.startsAt), day.endsAt - hours * 60);
    setPaged({ key, from: clamped });
  };
  return { window, page };
}

/**
 * The window a grid draws for what was chosen (CZ-D1, CZ-D4, CZ-D5): the paged
 * hours, a preset's half of the day, or null for the whole day. `pageable`
 * says whether the arrows and the wheel move it.
 */
export function useDayWindow(
  day: { startsAt: number; endsAt: number },
  zoom: Zoom,
  capacity: Capacity | null,
  date: string,
  nowMinute: number | null,
): {
  window: HourWindow | null;
  page: (direction: 1 | -1, by?: number) => void;
  pageable: boolean;
} {
  const paged = usePagedWindow(
    day,
    isPreset(zoom) ? null : effectiveHours(zoom, capacity),
    date,
    nowMinute,
  );
  if (zoom === "am" || zoom === "pm") {
    return { window: presetWindow(day, zoom), page: paged.page, pageable: false };
  }
  if (zoom === "full") return { window: null, page: paged.page, pageable: false };
  return { ...paged, pageable: paged.window !== null };
}
